/**
 * HealingCircleOpening — Ceremonial intro shown at the start of every
 * Spinal Healing & Recovery session.
 *
 * A circle of voice avatars gently glows around the words:
 *   "Today your Healing Circle is with you."
 *
 * After ~6 seconds the ritual dissolves and the main immersion begins.
 * If the recipient has only one voice (or the API is unreachable) the
 * ceremony gracefully shows a single avatar or skips itself.
 */

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, User, Check, Volume2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const token = localStorage.getItem('reclaim_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AVATAR_HUES = [
  'rgba(220,180,120,0.9)',
  'rgba(180,210,190,0.9)',
  'rgba(210,180,200,0.9)',
  'rgba(180,200,220,0.9)',
  'rgba(220,200,170,0.9)',
];

const HealingCircleOpening = ({ onDone, autoAdvanceMs = 6500 }) => {
  const [voices, setVoices] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState(null);
  // Which voice guides today's session. Falls back to Michael V3 when
  // no explicit selection exists. When the user taps an avatar we
  // persist the choice (POST /family/select) and update this state
  // so the ring highlights the chosen face.
  const [activeVoiceId, setActiveVoiceId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewingId, setPreviewingId] = useState(null);
  // User interaction cancels the auto-advance — she's now choosing.
  const [interacted, setInteracted] = useState(false);
  const previewRef = useRef(null);
  const audioRef = useState(() => ({ current: null }))[0];

  useEffect(() => {
    (async () => {
      try {
        const [vR, mR, cR] = await Promise.all([
          fetch(`${API}/api/healing/spinal-ease/family/voices`, { headers: authHeader() }),
          fetch(`${API}/api/healing/spinal-ease/messages/today`, { headers: authHeader() }),
          fetch(`${API}/api/healing/spinal-ease/voice-config-v2`, { headers: authHeader() }),
        ]);
        // Voices — the ceremonial ring MUST never be empty. If the
        // fetch fails or returns nothing, we synthesise a graceful
        // fallback with Michael V3 so the ceremony still opens.
        let loadedVoices = [];
        if (vR.ok) {
          const d = await vR.json();
          loadedVoices = d.voices || [];
        }
        if (!loadedVoices.length) {
          loadedVoices = [{
            id: 'fallback-michael-v3',
            speaker_name: 'Michael · V3',
            relation: 'Studio voice · Reclaim',
            is_synthetic: true,
            is_selected: true,
            audio_url: null,
          }];
        }
        setVoices(loadedVoices);
        if (cR.ok) {
          const cfg = await cR.json();
          if (cfg?.voice_id) setActiveVoiceId(cfg.voice_id);
        }
        if (mR.ok) {
          const d = await mR.json();
          if (d.message) {
            setMessage(d.message);
            // Play the audio after a short delay so the ceremony has settled.
            setTimeout(() => {
              try {
                const url = d.message.audio_url.startsWith('http')
                  ? d.message.audio_url
                  : `${API}${d.message.audio_url}`;
                audioRef.current = new Audio(url);
                audioRef.current.volume = 0.9;
                audioRef.current.play().catch(() => { /* autoplay blocked */ });
              } catch { /* ignore */ }
            }, 1500);
          }
        }
      } catch {
        // Still show a graceful intro with a single Michael V3 face
        // so the ring is never empty.
        setVoices([{
          id: 'fallback-michael-v3',
          speaker_name: 'Michael · V3',
          relation: 'Studio voice · Reclaim',
          is_synthetic: true,
          is_selected: true,
          audio_url: null,
        }]);
      }
      setLoaded(true);
    })();
    return () => {
      if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } audioRef.current = null; }
      if (previewRef.current) { try { previewRef.current.pause(); } catch { /* noop */ } previewRef.current = null; }
    };
  }, [audioRef]);

  useEffect(() => {
    if (!loaded) return;
    // If she starts choosing, don't auto-advance — she'll tap Enter herself.
    if (interacted) return;
    // If a message is playing, give it time to finish (~10s).
    const delay = message ? Math.max(autoAdvanceMs, 11000) : autoAdvanceMs;
    const t = setTimeout(() => onDone && onDone(), delay);
    return () => clearTimeout(t);
  }, [loaded, autoAdvanceMs, onDone, message, interacted]);

  const stopPreview = () => {
    if (previewRef.current) {
      try { previewRef.current.pause(); } catch { /* audio already stopped */ }
      previewRef.current = null;
    }
    setPreviewingId(null);
  };

  const selectVoice = async (v) => {
    if (saving) return;
    setInteracted(true);
    stopPreview();
    // Silence the daily message so the preview isn't muddled.
    if (audioRef.current) { try { audioRef.current.pause(); } catch { /* noop */ } }
    setSaving(true);
    const previousId = activeVoiceId;
    // Optimistic — reflects instantly, revert on failure.
    setActiveVoiceId(v.id);
    // Play a short preview of this voice.
    try {
      const url = v.audio_url?.startsWith('http') ? v.audio_url : `${API}${v.audio_url}`;
      previewRef.current = new Audio(url);
      previewRef.current.volume = 0.85;
      previewRef.current.onended = () => setPreviewingId(null);
      previewRef.current.onerror = () => setPreviewingId(null);
      setPreviewingId(v.id);
      previewRef.current.play().catch(() => setPreviewingId(null));
    } catch { setPreviewingId(null); }
    try {
      const r = await fetch(`${API}/api/healing/spinal-ease/family/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ voice_id: v.id }),
      });
      if (!r.ok) throw new Error('failed');
    } catch {
      // Revert on error.
      setActiveVoiceId(previousId);
    }
    setSaving(false);
  };

  // Position avatars around a circle.
  const positioned = voices.map((v, i) => {
    const angle = (i / Math.max(1, voices.length)) * Math.PI * 2 - Math.PI / 2;
    const radius = 130;
    return {
      ...v,
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      hue: AVATAR_HUES[i % AVATAR_HUES.length],
      delay: 0.4 + i * 0.28,
    };
  });

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse 100% 90% at 50% 50%, #0d0a12 0%, #030204 65%)',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
      data-testid="healing-circle-opening"
    >
      {/* Soft ambient dust */}
      {[...Array(24)].map((_, i) => (
        <motion.div
          key={`hcd-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 1.5 + (i % 3) * 0.7,
            height: 1.5 + (i % 3) * 0.7,
            left: `${8 + (i * 7) % 84}%`,
            top: `${8 + (i * 11) % 84}%`,
            background: 'rgba(240,220,180,0.7)',
            boxShadow: '0 0 4px rgba(240,220,180,0.4)',
          }}
          animate={{
            opacity: [0, 0.7, 0],
            y: [0, -10 - (i % 4) * 3, 0],
          }}
          transition={{
            duration: 8 + (i % 5),
            repeat: Infinity,
            delay: i * 0.4,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Central invitation text — pinned to the TOP so it doesn't cover
          the ring of tappable faces below it. */}
      <div className="absolute z-10 text-center px-8" style={{ top: '8%', left: '50%', transform: 'translateX(-50%)', maxWidth: 400 }}>
        <motion.p
          className="text-white/25 text-[10px] tracking-[0.4em] uppercase mb-3"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1.4 }}
        >
          Healing Circle
        </motion.p>
        <motion.h2
          className="text-white/85 text-xl md:text-2xl leading-snug"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1.6 }}
          data-testid="healing-circle-title"
        >
          Today your Healing Circle is with you.
        </motion.h2>
      </div>

      {/* Tap-to-choose hint — sits BELOW the ring so it clearly labels
          the tappable faces above it. */}
      {loaded && voices.length > 0 && (
        <motion.p
          className="absolute text-white/60 text-[11px] tracking-[0.28em] uppercase text-center px-6"
          style={{ fontFamily: "'Manrope', sans-serif", bottom: '18%', left: '50%', transform: 'translateX(-50%)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.6, duration: 1.4 }}
          data-testid="healing-circle-tap-hint"
        >
          ↑ Tap a face to let them guide you
        </motion.p>
      )}

      {/* Today's Healing Message — pinned above the ring on the LEFT so
          it doesn't collide with the tappable faces. Only visible when
          a family member has recorded a message for today. */}
      {message && (
        <motion.div
          className="absolute z-10 px-4 py-3 rounded-2xl flex flex-col items-center gap-1.5"
          style={{
            background: 'rgba(200,170,110,0.08)',
            border: '1px solid rgba(200,170,110,0.28)',
            maxWidth: 260,
            top: '22%',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.5, duration: 1.2 }}
          data-testid="todays-message"
        >
          <p className="text-white/40 text-[9px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>
            A message from {message.speaker_name || 'a loved one'}
          </p>
          {message.text && (
            <p className="text-white/80 text-[12px] text-center mt-1" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              &ldquo;{message.text}&rdquo;
            </p>
          )}
          <div className="flex gap-1 mt-1.5">
            {[0, 1, 2].map((k) => (
              <motion.span
                key={k}
                className="w-0.5 h-3 rounded-full"
                style={{ background: 'rgba(220,190,140,0.85)' }}
                animate={{ scaleY: [0.3, 1, 0.3] }}
                transition={{ duration: 0.9, repeat: Infinity, delay: k * 0.2, ease: 'easeInOut' }}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Circle of avatars */}
      <div className="absolute flex items-center justify-center" style={{ width: 320, height: 320 }}>
        {/* Faint outer ring */}
        <motion.div
          className="absolute rounded-full border pointer-events-none"
          style={{
            width: 300, height: 300,
            borderColor: 'rgba(200,170,110,0.15)',
          }}
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 1.4 }}
        />
        <motion.div
          className="absolute rounded-full border pointer-events-none"
          style={{
            width: 360, height: 360,
            borderColor: 'rgba(200,170,110,0.06)',
            borderStyle: 'dashed',
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
        />

        {positioned.map((v) => {
          const isChosen = v.id === activeVoiceId;
          const isPreviewing = v.id === previewingId;
          return (
            <motion.button
              key={v.id}
              type="button"
              onClick={() => selectVoice(v)}
              className="absolute flex flex-col items-center focus:outline-none"
              style={{ touchAction: 'manipulation', background: 'transparent', border: 0, padding: 0 }}
              initial={{ opacity: 0, scale: 0.5, x: v.x, y: v.y }}
              animate={{ opacity: 1, scale: 1, x: v.x, y: v.y }}
              transition={{ delay: v.delay, duration: 0.9, ease: 'easeOut' }}
              whileHover={{ scale: 1.06, x: v.x, y: v.y }}
              whileTap={{ scale: 0.92, x: v.x, y: v.y }}
              data-testid={`healing-circle-avatar-${v.id}`}
              aria-label={`Choose ${v.speaker_name} as today's voice`}
            >
              {/* Halo — brighter when chosen or previewing */}
              <motion.div
                className="absolute rounded-full"
                style={{
                  width: isChosen ? 84 : 64,
                  height: isChosen ? 84 : 64,
                  background: `radial-gradient(circle, ${v.hue.replace('0.9', isChosen ? '0.6' : '0.35')} 0%, transparent 70%)`,
                  filter: `blur(${isChosen ? 12 : 8}px)`,
                  top: isChosen ? -10 : 0,
                }}
                animate={
                  isPreviewing
                    ? { opacity: [0.7, 1, 0.7], scale: [1, 1.18, 1] }
                    : { opacity: [0.55, 0.95, 0.55], scale: [0.95, 1.08, 0.95] }
                }
                transition={{ duration: isPreviewing ? 1.4 : (3 + (v.delay % 1.5)), repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* Avatar bubble */}
              <div
                className="relative w-11 h-11 rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(20,16,14,0.85)',
                  border: `${isChosen ? 2 : 1}px solid ${v.hue.replace('0.9', isChosen ? '1' : '0.9')}`,
                  boxShadow: `0 0 ${isChosen ? 32 : 18}px ${v.hue.replace('0.9', isChosen ? '0.6' : '0.35')}`,
                  color: v.hue,
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: 'italic',
                  fontSize: 15,
                }}
              >
                {v.is_synthetic ? <Sparkles size={15} /> : (v.speaker_name?.[0] || <User size={14} />)}
                {/* Chosen tick */}
                {isChosen && (
                  <div
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ background: v.hue.replace('0.9', '1'), color: '#0d0a12' }}
                    data-testid={`healing-circle-chosen-${v.id}`}
                  >
                    <Check size={9} strokeWidth={3} />
                  </div>
                )}
                {/* Preview indicator */}
                {isPreviewing && (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(20,16,14,0.95)', color: v.hue }}>
                    <Volume2 size={9} />
                  </div>
                )}
              </div>
              {/* Name label */}
              <p
                className="mt-2 text-[10px] tracking-[0.2em] uppercase"
                style={{
                  color: v.hue,
                  fontFamily: "'Manrope', sans-serif",
                  textShadow: `0 0 8px ${v.hue.replace('0.9', '0.35')}`,
                  fontWeight: isChosen ? 600 : 400,
                }}
              >
                {v.speaker_name}
              </p>
            </motion.button>
          );
        })}

        {/* Central pulse — the recipient */}
        <motion.div
          className="w-24 h-24 rounded-full flex items-center justify-center"
          style={{
            background: 'radial-gradient(circle, rgba(255,240,200,0.35) 0%, rgba(220,180,110,0.15) 45%, transparent 100%)',
            border: '1px solid rgba(255,240,200,0.4)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Continue button — labels the chosen voice, so she knows who is guiding. */}
      {(() => {
        const chosen = voices.find(v => v.id === activeVoiceId);
        const label = chosen
          ? `Continue with ${chosen.speaker_name} →`
          : 'Enter →';
        return (
          <button
            onClick={() => { stopPreview(); onDone && onDone(); }}
            className="absolute bottom-8 right-8 text-white/50 text-[10px] tracking-[0.3em] uppercase hover:text-white/80 transition-colors"
            style={{ fontFamily: "'Manrope', sans-serif", maxWidth: '65%', textAlign: 'right' }}
            data-testid="healing-circle-skip"
          >
            {label}
          </button>
        );
      })()}
    </motion.div>
  );
};

export default HealingCircleOpening;
